import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBanners, fetchHomepage, fetchLobbySections, type LobbySections } from '../api/catalog';
import { listRecent } from '../api/playerGames';
import type { Banner, Game } from '../api/types';
import { BannerCarousel } from '../components/BannerCarousel';
import { CampaignCards } from '../components/CampaignCards';
import { CategoryChips, type GameCategory } from '../components/CategoryChips';
import { GameGrid } from '../components/GameGrid';
import { GameSectionRow } from '../components/GameSectionRow';
import { LobbySearch } from '../components/LobbySearch';
import { useAuth } from '../auth/AuthContext';
import { useCatalog } from '../hooks/useCatalog';
import { isSlotCategory, restoreCatalogPosition } from '../lib/gameLaunch';

const EMPTY_SECTIONS: LobbySections = {
  catalog_total: 0,
  slots: [],
  popular: [],
  recommended: [],
  new: [],
  fast: [],
  bonus: [],
};

export function HomePage() {
  const { t, i18n } = useTranslation(['common', 'games', 'nav']);
  const { user } = useAuth();
  const catalog = useCatalog();
  const [layout, setLayout] = useState<Record<string, boolean>>({});
  const [autoplayMs, setAutoplayMs] = useState(5000);
  const [sections, setSections] = useState<LobbySections>(EMPTY_SECTIONS);
  const [history, setHistory] = useState<Game[]>([]);
  const [campaignCards, setCampaignCards] = useState<Banner[]>([]);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [category, setCategory] = useState<GameCategory>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const locale = (i18n.resolvedLanguage || i18n.language || 'az').split('-')[0];

  useEffect(() => {
    restoreCatalogPosition();
  }, []);

  useEffect(() => {
    void fetchHomepage()
      .then((data) => {
        setLayout(data.sections || {});
        if (data.banner_autoplay_ms) setAutoplayMs(data.banner_autoplay_ms);
      })
      .catch(() => {
        setLayout({
          hero: true,
          stories: true,
          featured_games: true,
          categories: true,
          providers: false,
          vip_card: true,
          announcement: false,
        });
      });
    void fetchLobbySections()
      .then((data) => setSections(data))
      .catch(() => setSections(EMPTY_SECTIONS));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCampaignCards([]);
    setCampaignLoading(true);
    void fetchBanners({ banner_type: 'campaign', locale })
      .then((data) => {
        if (!cancelled) setCampaignCards(data.banners || []);
      })
      .catch(() => {
        if (!cancelled) setCampaignCards([]);
      })
      .finally(() => {
        if (!cancelled) setCampaignLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    if (!user) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    void listRecent()
      .then((data) => {
        const resolved: Game[] = [];
        for (const entry of data.recent || []) {
          if (entry.game) resolved.push(entry.game);
          if (resolved.length >= 24) break;
        }
        if (!cancelled) setHistory(resolved);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const slotGames = sections.slots;
  const popularGames = sections.popular;
  const recommended = sections.recommended;
  const newest = sections.new;
  const fastGames = sections.fast;
  const bonusGames = sections.bonus;
  const gridGames = useMemo(() => {
    const base = category === 'slots' ? catalog.games.filter(isSlotCategory) : catalog.games;
    return base.slice(0, 48);
  }, [catalog.games, category]);

  const showHero = layout.hero !== false;
  const showStories = layout.stories !== false;
  const searching = searchOpen && Boolean(catalog.query.trim());
  const heroBanners = catalog.banners.filter((b) => (b.banner_type || 'hero') === 'hero');

  return (
    <div className="page home-page">
      {showStories || showHero ? (
        <section className="home-promo-stage" aria-label={t('common:promoFallbackTitle')}>
          {showStories ? (
            <div className="home-promo-stories">
              <CampaignCards cards={campaignCards} loading={campaignLoading} />
            </div>
          ) : null}

          {showHero ? (
            <div className="lobby-hero home-promo-hero">
              {heroBanners.length > 0 || catalog.bannerLoading ? (
                <BannerCarousel
                  banners={heroBanners}
                  loading={catalog.bannerLoading}
                  autoplayMs={autoplayMs}
                />
              ) : (
                <article className="lobby-hero-fallback">
                  <p className="eyebrow">{t('common:heroEyebrow')}</p>
                  <h1>{t('common:heroTitle')}</h1>
                  <p>{t('common:heroSubtitle')}</p>
                  <Link to="/games" className="btn btn-primary btn-play">
                    {t('common:heroCta')}
                  </Link>
                </article>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="lobby-tools lobby-tools-compact">
        <LobbySearch
          open={searchOpen}
          value={catalog.query}
          onOpenChange={(open) => {
            setSearchOpen(open);
            if (!open) catalog.setQuery('');
          }}
          onChange={catalog.setQuery}
        />
        {!searching ? (
          <CategoryChips
            value={category}
            onChange={(value) => {
              setCategory(value);
              catalog.setCategory(value);
            }}
          />
        ) : null}
      </div>

      {searching ? (
        <>
          <div className="section-head">
            <h2>{t('games:searchResults', { defaultValue: 'Axtarış' })}</h2>
            <span className="muted-note">{t('games:results', { count: catalog.total })}</span>
          </div>
          <GameGrid
            games={catalog.games}
            loading={catalog.gameLoading}
            error={catalog.error}
            emptyMessage={catalog.message || t('games:emptyTitle')}
            onRetry={catalog.reload}
          />
          {!catalog.gameLoading && catalog.hasMore ? (
            <div className="catalog-load-more">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={catalog.loadingMore}
                onClick={catalog.loadMore}
              >
                {catalog.loadingMore ? t('games:loadingMore') : t('games:loadMore')}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {history.length > 0 ? (
            <GameSectionRow
              title={t('games:history', { defaultValue: 'Oyun tarixçəsi' })}
              games={history}
              seeAllTo="/games?section=history"
            />
          ) : null}

          {slotGames.length > 0 ? (
            <GameSectionRow title={t('games:slots')} games={slotGames} seeAllTo="/games?section=slots" />
          ) : null}

          {popularGames.length > 0 ? (
            <GameSectionRow
              title={t('games:popular')}
              games={popularGames}
              seeAllTo="/games?section=popular"
            />
          ) : null}

          {recommended.length > 0 ? (
            <GameSectionRow
              title={t('games:recommended', { defaultValue: 'Tövsiyə olunanlar' })}
              games={recommended}
              seeAllTo="/games?section=recommended"
            />
          ) : null}

          {newest.length > 0 ? (
            <GameSectionRow title={t('games:new')} games={newest} seeAllTo="/games?section=new" />
          ) : null}

          {fastGames.length > 0 ? (
            <GameSectionRow
              title={t('games:fastGames', { defaultValue: 'Sürətli oyunlar' })}
              games={fastGames}
              seeAllTo="/games?section=fast"
            />
          ) : null}

          {bonusGames.length > 0 ? (
            <GameSectionRow
              title={t('games:bonusWager', { defaultValue: 'Bonusla mərc' })}
              games={bonusGames}
              seeAllTo="/games?section=bonus"
            />
          ) : null}

          <div className="section-head">
            <div>
              <h2>{category === 'slots' ? t('games:slots') : t('games:lobbySection')}</h2>
            </div>
            <div className="section-head-actions">
              {catalog.total > 0 ? (
                <span className="muted-note">{t('games:results', { count: catalog.total })}</span>
              ) : null}
              <Link to="/games?section=all" className="btn btn-ghost">
                {t('common:seeAll')}
              </Link>
            </div>
          </div>
          <GameGrid
            games={gridGames}
            loading={catalog.gameLoading}
            error={catalog.error}
            emptyMessage={catalog.message}
            onRetry={catalog.reload}
          />
        </>
      )}
    </div>
  );
}
